import { memo, useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cast, handleApiError, notify } from '~/utils/helper';
import { CloseIcon } from '~/assets/icons';
import locationApi from '~/apis/location';
import { ILocation } from '~/utils/interface/location';
import { useAppDispatch } from '~/redux/hooks';
import { IInputGeofencePerson, IPerson } from '~/utils/interface/person';
import { useFormik } from 'formik';
import { commonActions } from '~/thunks/common/commonSlice';
import { IUser } from '~/utils/interface/user';
import { handleUnassgignGeofence } from '~/thunks/person/personThunk';
import { BaseScrollbar } from '../common';

type UnAssignLocationDialogProps = {
  data?: IPerson;
  currentUser?: IUser;
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IInputGeofencePerson = {
  person_id: '',
  geofence_ids: [],
};

const UnAssignLocationDialog = (props: UnAssignLocationDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/location');

  const [locations, setLocations] = useState<ILocation[]>([]);

  useEffect(() => {
    if (!props.open || !props.currentUser?.username) return;

    (async () => {
      try {
        dispatch(commonActions.setIsLoading(true));
        const response = await locationApi.getList({
          username: props.currentUser?.username,
          getall: true,
        });
        setLocations(response.data.result as any);
      } catch (e: any) {
        handleApiError(e, true);
      } finally {
        dispatch(commonActions.setIsLoading(false));
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.currentUser]);

  useEffect(() => {
    if (!props.open || !props.data) return;

    form.setFieldValue('person_id', String(props.data.id));
    form.setFieldValue('geofence_ids', props.data.geofence_ids || []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        await dispatch(handleUnassgignGeofence(values)).unwrap();
        notify('success', t('assign.message.remove', { cccd: cast(props.data?.cccd) }));
        handleSetOpen(false, true);
      } catch (e: any) {
        handleApiError(e);
      } finally {
        dispatch(commonActions.setIsLoading(false));
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const renderSelectOptions = () => {
    return locations
      ?.filter((item: ILocation) => props.data?.geofence_ids?.includes(cast(item.id)))
      .map((item: ILocation) => (
        <Box key={item.id} className='flex items-center w-[98%] mb-2'>
          <Checkbox
            checked={form.values.geofence_ids.includes(cast(item.id))}
            onChange={(e) => {
              if (e.target.checked) {
                form.setFieldValue('geofence_ids', [...(form.values.geofence_ids || []), item.id]);
              } else {
                form.setFieldValue(
                  'geofence_ids',
                  form.values.geofence_ids.filter((id) => id !== cast(item.id))
                );
              }
            }}
            className='mr-1.5'
            sx={{
              color: '#2E89C8',
              '&.Mui-checked': { color: '#2E89C8' },
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          />
          <Box className='w-full p-0 border-0'>
            <Box className='flex justify-between items-center border rounded-xl w-full h-12 px-4'>
              <Typography className='text-sm text-[#404854]'>{cast(item.name)}</Typography>
              <Typography className='text-sm text-[#404854]'>
                {item.province_name ? item.province_name : ''}
                {item.ward_name ? ' - ' + item.ward_name : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      ));
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {t('assign.remove')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className='mb-2'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
            {t('cccd.text', { ns: 'admin/person' })}
          </Typography>
          <Typography className='bg-[#D7DEE7] text-base text-[#7B8794] rounded-xl h-12 px-4 leading-[48px]'>
            {cast(props.data?.cccd)}
          </Typography>
        </Box>
        <Box className='mb-2'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>
            {t('full_name.title', { ns: 'admin/person' })}
          </Typography>
          <Typography className='bg-[#D7DEE7] text-base text-[#7B8794] rounded-xl h-12 px-4 leading-[48px]'>
            {cast(props.data?.full_name)}
          </Typography>
        </Box>
        <Box component='form'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('assign.text')}</Typography>
          <BaseScrollbar maxHeight='500px'>{renderSelectOptions()}</BaseScrollbar>
          <Box className='w-full'>
            <Box className='flex justify-between gap-4'>
              <Button
                variant='outlined'
                className='flex-1 bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
                onClick={() => handleSetOpen(false)}
              >
                {t('cancel', { ns: 'admin/common' })}
              </Button>
              <Button
                variant='contained'
                disabled={form.values.geofence_ids.length === 0}
                className='flex-1 bg-[#123B6D] text-base font-semibold'
                onClick={() => form.handleSubmit()}
              >
                {t('unassignLocation', { ns: 'admin/common' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(UnAssignLocationDialog);
